// SCR-STAFF-901 — چک‌لیست راه‌اندازی و Go-Live (US-PLT-052، 054): درصد پیشرفت محاسبه‌شده از داده ماژول‌ها (بدون اقدام کاربر —
// AC-PLT-052.1)، آیتم‌های فقط‌چک‌لیست (اتصال پیامک، محتوای نمونه)، و اعلام شروع کار با پذیرش صریح شرایط استفاده (نسخه جاری و
// نشان «نمونه» — CLAUDE.md §10) که فقط مالک (plt.subscription.manage) پس از تکمیل مراحل الزامی می‌بیند (C-AGENT-418).
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type OnboardingStepView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, JalaliDate, ListPage, LoadingState, SampleBadge } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { onboardingKeys, SETUP_PATH } from '../setup/common';
import { pltKeys, toCount } from './common';

export const SCR_STAFF_901 = 'SCR-STAFF-901';
export const CHECKLIST_PATH = '/settings/checklist';
export const GO_LIVE_PERMISSION = 'plt.subscription.manage';

function stateLabel(item: OnboardingStepView): string {
  return `plt:setup.state.${item.state}`;
}

function GoLiveDialog({
  open,
  version,
  isSample,
  onClose,
}: {
  open: boolean;
  version: string;
  isSample: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const titleId = useId();
  const [accepted, setAccepted] = useState(false);

  const goLive = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/platform/onboarding:go-live', {
          body: { acceptTerms: accepted, termsVersion: version },
        }),
      ),
    onSuccess: async (view) => {
      queryClient.setQueryData(pltKeys.goLive(session?.tenantId), view);
      await queryClient.invalidateQueries({ queryKey: pltKeys.checklist(session?.tenantId) });
      // مرحله go_live در Setup Wizard هم کامل می‌شود.
      await queryClient.invalidateQueries({ queryKey: onboardingKeys.view(session?.tenantId) });
      onClose();
    },
  });

  return (
    <Dialog
      open={open}
      onClose={goLive.isPending ? undefined : onClose}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id={titleId}>{t('plt:checklist.go_live.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>{t('plt:checklist.go_live.description')}</Typography>
          <Paper variant="outlined" sx={{ p: 2 }} data-testid="terms-box">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">
                {t('plt:checklist.go_live.terms_title', { version: faDigits(version) })}
              </Typography>
              {isSample && <SampleBadge />}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t('plt:checklist.go_live.terms_summary')}
            </Typography>
          </Paper>
          <FormControlLabel
            control={
              <Checkbox
                checked={accepted}
                onChange={(event) => setAccepted(event.target.checked)}
                data-testid="accept-terms"
              />
            }
            label={t('plt:checklist.go_live.accept', { version: faDigits(version) })}
          />
          {goLive.error ? <ErrorState error={goLive.error} compact /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={goLive.isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => goLive.mutate()}
          disabled={!accepted || goLive.isPending}
          data-testid="go-live-confirm"
        >
          {t('plt:checklist.go_live.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** فهرست بازبینی محتوای نمونه (SCR-STAFF-1203). */
const SAMPLE_REVIEW_PATH = '/settings/specialty-packs?tab=samples';

export function ChecklistPage() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const [dialog, setDialog] = useState(false);
  const canGoLive = access.can(GO_LIVE_PERMISSION) && access.accessMode === 'Full';

  const checklist = useQuery({
    queryKey: pltKeys.checklist(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/onboarding/checklist')),
  });
  const goLive = useQuery({
    queryKey: pltKeys.goLive(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/onboarding/go-live')),
  });

  const data = checklist.data;
  const live = goLive.data;
  const percent = toCount(data?.percent);

  return (
    <ListPage screenId={SCR_STAFF_901} titleKey="plt:checklist.title" subtitleKey="plt:checklist.subtitle">
      {checklist.isPending ? (
        <LoadingState rows={4} />
      ) : checklist.error ? (
        <ErrorState error={checklist.error} onRetry={() => void checklist.refetch()} />
      ) : data ? (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" data-testid="checklist-percent">
                {t('plt:checklist.progress', {
                  percent: faDigits(percent),
                  done: faDigits(toCount(data.completedItems)),
                  total: faDigits(toCount(data.availableItems)),
                })}
              </Typography>
              <Button component={RouterLink} to={SETUP_PATH} size="small">
                {t('plt:checklist.open_wizard')}
              </Button>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={percent}
              aria-label={t('plt:checklist.progress_label')}
            />
          </Paper>
          <Paper variant="outlined">
            <List aria-label={t('plt:checklist.items_label')} data-testid="checklist-items">
              {data.items.map((item) => (
                <ListItem
                  key={item.key}
                  data-testid={`checklist-item-${item.key}`}
                  data-state={item.state}
                  secondaryAction={
                    <Stack direction="row" spacing={1}>
                      {/* AP-CFG-01 (FLOW-16): «بازبینی محتوای نمونه (N مورد)» ← فهرست اقلام نمونه. */}
                      {item.key === 'sample_content' && toCount(item.count) > 0 && (
                        <Button component={RouterLink} to={SAMPLE_REVIEW_PATH} size="small" data-testid="checklist-sample-review">
                          {t('plt:checklist.sample_review', { count: faDigits(toCount(item.count)) })}
                        </Button>
                      )}
                      {data.checklistOnlyKeys.includes(item.key) && (
                        <Chip size="small" variant="outlined" label={t('plt:checklist.checklist_only')} />
                      )}
                      <Chip
                        size="small"
                        color={
                          item.isComplete ? 'success' : item.state === 'ComingSoon' ? 'default' : 'warning'
                        }
                        label={t(stateLabel(item))}
                      />
                    </Stack>
                  }
                >
                  <ListItemIcon>
                    {item.isComplete ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <RadioButtonUncheckedIcon color="disabled" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(`plt:checklist.items.${item.key}`, {
                      defaultValue: t(`plt:setup.steps.${item.key}.title`, { defaultValue: item.key }),
                    })}
                    secondary={item.isRequired ? t('plt:checklist.required') : t('plt:setup.optional')}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }} data-testid="go-live-card">
            <Stack direction="row" spacing={2} alignItems="center">
              <RocketLaunchOutlinedIcon color={live?.wentLiveAt ? 'success' : 'action'} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1">{t('plt:checklist.go_live.card_title')}</Typography>
                {live?.wentLiveAt ? (
                  <Typography color="success.main" data-testid="went-live-at">
                    {t('plt:checklist.go_live.done')} <JalaliDate value={live.wentLiveAt} withTime />
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {data.requiredComplete
                      ? t('plt:checklist.go_live.ready')
                      : t('plt:checklist.go_live.not_ready')}
                  </Typography>
                )}
              </Box>
              {!live?.wentLiveAt && (
                <Button
                  variant="contained"
                  onClick={() => setDialog(true)}
                  disabled={!canGoLive || !data.requiredComplete || !live}
                  data-testid="go-live-open"
                >
                  {t('plt:checklist.go_live.open')}
                </Button>
              )}
            </Stack>
            {!canGoLive && !live?.wentLiveAt && (
              <Alert severity="info" sx={{ mt: 2 }} data-testid="go-live-owner-only">
                {t('plt:checklist.go_live.owner_only')}
              </Alert>
            )}
          </Paper>
          {live && (
            <GoLiveDialog
              open={dialog}
              version={live.currentTermsVersion}
              isSample={live.termsIsSample}
              onClose={() => setDialog(false)}
            />
          )}
        </Stack>
      ) : null}
    </ListPage>
  );
}
